import { useEffect, useState } from "react"
import { Navigate, useNavigate } from "react-router-dom"
import { useAuth } from "../context/userContext"

const API = process.env.REACT_APP_API_URL

/* Shown after sign-up (and whenever an unconfirmed account opens its account page) */
const VerifyNotice = () => {
  const { user, setUser, refreshUser } = useAuth()
  const navigate = useNavigate()
  const [sending, setSending] = useState(false)
  const [checking, setChecking] = useState(false)
  const [message, setMessage] = useState(null)
  const [wait, setWait] = useState(0)

  /* Count down before "Resend" can be used again */
  useEffect(() => {
    if (wait <= 0) return
    const t = setTimeout(() => setWait((w) => w - 1), 1000)
    return () => clearTimeout(t)
  }, [wait])

  if (user === undefined) return null
  if (!user) return <Navigate to="/login" replace />
  if (user.email_verified) return <Navigate to="/account" replace />

  const resend = async () => {
    try {
      setSending(true)
      setMessage(null)
      const res = await fetch(`${API}/resend-verification`, { method: "POST", credentials: "include" })
      const data = await res.json().catch(() => ({}))
      if (res.status === 429) {
        setWait(data.retryAfter || 60)
        return setMessage({ ok: false, text: data.message })
      }
      if (!res.ok) throw new Error(data.message || "Couldn't send the email")
      setWait(60)
      setMessage({ ok: true, text: `We sent a new link to ${user.email}.` })
    } catch (err) {
      setMessage({ ok: false, text: err.message })
    } finally {
      setSending(false)
    }
  }

  // For when they confirmed in another tab or on their phone
  const checkAgain = async () => {
    setChecking(true)
    await refreshUser()
    setChecking(false)
    setMessage({ ok: false, text: "Not confirmed yet — click the link in the email first." })
  }

  const logout = async () => {
    await fetch(`${API}/logout`, { method: "POST", credentials: "include" }).catch(() => {})
    setUser(null)
    navigate("/signup")
  }

  return (
    <div className="d-flex justify-content-center align-items-center px-3" style={{ minHeight: "70vh" }}>
      <div className="text-center border rounded-3 bg-light p-4 p-md-5" style={{ maxWidth: 520 }}>
        <div className="fs-1 mb-2" aria-hidden="true">✉️</div>
        <h1 className="h2 mb-3">Confirm your email</h1>
        <p className="mb-1">We sent a confirmation link to</p>
        <p className="fw-semibold mb-3 text-break">{user.email}</p>
        <p className="text-muted small mb-4">
          Click the link in that email to finish setting up your account. It can take a minute to arrive —
          check your spam or promotions folder too. The link works for 24 hours.
        </p>

        <div className="d-grid gap-2">
          <button className="btn btn-success" onClick={checkAgain} disabled={checking}>
            {checking ? "Checking…" : "I've confirmed my email"}
          </button>
          <button className="btn btn-outline-secondary" onClick={resend} disabled={sending || wait > 0}>
            {sending ? "Sending…" : wait > 0 ? `Resend email (${wait}s)` : "Resend email"}
          </button>
        </div>

        <p className={`mt-3 mb-0 small ${message?.ok ? "text-success" : "text-danger"}`} role="status">
          {message?.text}
        </p>

        <hr className="my-4" />
        <p className="small text-muted mb-0">
          Wrong email address?{" "}
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={logout}>
            Sign up again
          </button>
        </p>
      </div>
    </div>
  )
}

export default VerifyNotice
